import { useEffect, useState } from "react";
import { Building2, Store, Activity, DollarSign, CheckCircle2, Clock } from "lucide-react";
import api from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";

const fmt = (n) => `$${Number(n || 0).toLocaleString("en-US")}`;

const KPI = ({ label, value, sub, subClass }) => (
  <div className="px-4 py-4 border-l border-slate-100 first:border-l-0">
    <div className="text-[11px] uppercase tracking-wide font-semibold text-slate-400">{label}</div>
    <div className="text-[24px] font-extrabold tracking-tight mt-2">{value}</div>
    <div className={`text-[12px] mt-0.5 ${subClass || "text-slate-400"}`}>{sub}</div>
  </div>
);

export default function Dashboard() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/staff/dashboard").then((r) => setData(r.data));
  }, []);

  if (!data) return <div className="p-8 text-slate-400 text-sm">Loading…</div>;

  return (
    <div className="p-6 md:p-8" data-testid="staff-dashboard">
      <h1 className="text-2xl font-extrabold tracking-tight">Platform Overview</h1>
      <p className="text-slate-500 text-sm mt-1">ScapeSource network health, onboarding, and revenue at a glance.</p>

      <div className="grid grid-cols-2 md:grid-cols-6 mt-6 border-t border-b border-slate-100">
        <KPI label="Active Contractors" value={data.kpis.active_contractors} sub="Active" />
        <KPI label="Active Vendors" value={data.kpis.active_vendors} sub="Approved" />
        <KPI label="Monthly Transactions" value={data.kpis.monthly_transactions} sub="POs total" />
        <KPI label="Platform Revenue" value={`$${data.kpis.platform_revenue.toLocaleString("en-US")}`} sub="MTD" />
        <KPI label="QB Sync Rate" value={`${data.kpis.qb_sync_rate}%`} sub="Healthy" subClass="text-emerald-600" />
        <KPI label="Vendor Queue" value={data.kpis.vendor_queue} sub="Pending approval" subClass="text-amber-600" />
      </div>

      <div className="grid md:grid-cols-2 gap-9 mt-8">
        <div>
          <div className="text-[15px] font-bold">Vendor Onboarding Queue</div>
          <div className="mt-3 space-y-0">
            {data.vendor_queue.map((v) => (
              <div key={v.id} className="flex items-center gap-3 py-3 border-t border-slate-100" data-testid={`queue-item-${v.id}`}>
                <div className="w-9 h-9 rounded-lg bg-sky-50 text-sky-700 flex items-center justify-center text-[11px] font-bold flex-shrink-0">{v.name.slice(0, 2).toUpperCase()}</div>
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-semibold truncate">{v.name}</div>
                  <div className="text-[11.5px] text-slate-400">{v.category}</div>
                </div>
              </div>
            ))}
            {data.vendor_queue.length === 0 && <div className="text-slate-400 text-sm py-4">Queue is clear.</div>}
          </div>
        </div>
        <div className="md:pl-9 md:border-l border-slate-100">
          <div className="text-[15px] font-bold">Integration Health</div>
          <div className="mt-3 space-y-2.5">
            {data.integration_health.map((h) => (
              <div key={h.name} className="flex items-center gap-3 border border-slate-100 rounded-lg px-3.5 py-2.5">
                {h.status === "healthy" ? <CheckCircle2 size={14} className="text-emerald-500" /> : <Clock size={14} className="text-amber-500" />}
                <div className="flex-1">
                  <div className="text-[13px] font-semibold">{h.name}</div>
                  <div className="text-[11.5px] text-slate-400">{h.description}</div>
                </div>
                <StatusBadge status={h.status === "healthy" ? "healthy" : "pending"} />
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="mt-8 pt-7 border-t border-slate-100">
        <div className="text-[15px] font-bold">Recent Platform Activity</div>
        <div className="mt-3 space-y-2.5">
          {data.activity.map((a) => (
            <div key={a.id} className="text-[12.5px] border-t border-slate-100 pt-2.5 flex gap-2">
              <Activity size={12} className="text-sky-600 mt-0.5 flex-shrink-0" /> {a.action}
            </div>
          ))}
          {data.activity.length === 0 && <div className="text-slate-400 text-sm">No activity yet.</div>}
        </div>
      </div>
    </div>
  );
}
